import type { Location } from '@models/location';
import type { Lookup } from '@models/lookup';
import type { Org } from '@models/org';
import type { Role } from '@models/role';
import type { Scope } from '@models/scope';
import type { User } from '@models/user';
import { findScopeItem, getScopeOrgIds } from '@util/orgScope';

export default function calcClaims(
  state: {
    user: User | undefined;
    roles: Lookup<Role> | undefined;
    locations: Lookup<Location> | undefined;
    orgs: Lookup<Org> | undefined;
  },
  scope: Scope[],
) {
  const anyScope = scope.includes('any');
  const activeOrgIds = anyScope
    ? new Set()
    : new Set(
        scope
          .flatMap((id) => {
            // A location is covered by its parent tenant and its groups, a group by its parent tenant
            const item = findScopeItem(id, state.locations, state.orgs);
            return [id, ...(item ? getScopeOrgIds(item) : [])];
          })
          .concat('global'),
      );

  const activeRoles =
    state.user?.orgRoles
      .filter((role) => anyScope || activeOrgIds.has(role.orgId))
      .flatMap((role) => state.roles?.get(role.roleId) ?? []) ?? [];

  // Any role reaching a location is enough to see it, even one without claims - mirrors the backend's CalcClaims
  const activeClaims = activeRoles.flatMap((role) => ['locations/read', ...role.claims]);

  return [...new Set<string>(activeClaims)];
}
